/* eslint-disable no-console */
import React from 'react'
import {
  allContactTypes,
  contactsFilteredByTypeRules
} from 'utils/contactTypes'
import {
  allFinancialAccountTypes,
} from 'utils/financialAccountTypes'
import {
  createContactFinancialAccountRole,
  deleteContactFinancialAccountRole,
  updateContactFinancialAccountRole,
  updateFinancialAccount,
} from 'utils/apiRequest'
import ContactIndexForm from './ContactIndexForm/ContactIndexForm'
import withContactIndexForm from './ContactIndexForm/withContactIndexForm'
import PropTypes from 'prop-types'
import {
  continueToNextSection,
  getPreviousSection,
} from 'utils/financialAccountTypeNavigation'
import { maskRepeatedContacts } from 'utils/contactUtils'
import { SCHWAB_IRA } from 'utils/financialAccountTypes.js'

const getJointOwner = (jointOwners, financialAccountId) => {
  const jointOwner = jointOwners && jointOwners.find(jointOwner =>
    jointOwner.financialAccountId === financialAccountId)
  return (jointOwner && jointOwner.data && jointOwner.data.attributes) || null
}

const ContactsSection = withContactIndexForm(ContactIndexForm)
const  ContactIndexBase  = (props) => {
  const {
    currentContacts,
    currentFinancialAccountContacts,
    editFinancialAccount,
    financialAccounts,
    goOnboardingAddContact,
    isMobile,
    jointOwners,
    location: {
      payload: {
        financialAccountId,
        financialAccountType,
        contactType,
      }
    },
    openModal,
    prospect,
    currentAccount,
  } = props
  const isCharitable = financialAccountType === 'schwab_charitable'
  const isBeneficiary = contactType === 'beneficiaries'
  const isSuccessorDonor = isCharitable && isBeneficiary
  const contactTypeConfig = (
    isSuccessorDonor
      ? allContactTypes.successor_donor
      : allContactTypes[contactType]
  )
  const { description, role } = contactTypeConfig
  const adHocContacts = currentFinancialAccountContacts.filter(c => c.role === role)
  const financialAccountTypeConfig = allFinancialAccountTypes[financialAccountType]
  const { section } = contactTypeConfig
  const title = contactTypeConfig.DISPLAY

  const handleContinueToNextSection = () => {
    const contactType = contactTypeConfig.reducerType
    continueToNextSection({
      financialAccounts,
      currentFinancialAccountId: financialAccountId,
      currentFinancialAccountType: financialAccountType,
      currentSection: contactType,
    })
  }

  const handleCreateContactRole = async (params) => {
    const {
      contactId,
      sharePercentage = null,
      beneficiaryType,
    } = params
    const { role } = contactTypeConfig
    const financialAccountRoleParams = {
      financialAccount: financialAccountId,
      sharePercentage,
      beneficiaryType,
      relatedContact: contactId,
      role,
    }
    const contactRole = await createContactFinancialAccountRole(financialAccountRoleParams)
    return contactRole
  }

  const handleUpdateContactRole = async (contactFinancialAccountRoleId, data) => {
    const updatedContactRole =
      await updateContactFinancialAccountRole(contactFinancialAccountRoleId, data)
    return updatedContactRole
  }

  const handleDeleteContactRole = async (contactId) => {
    const contact = adHocContacts.find(c => c.id === contactId)
    const { contactFinancialAccountRoleId } = contact

    await deleteContactFinancialAccountRole(contactFinancialAccountRoleId)
    return contactFinancialAccountRoleId
  }

  const prevPageInfo = getPreviousSection({
    currentFinancialAccountId: financialAccountId,
    currentFinancialAccountType: financialAccountType,
    currentSection: contactTypeConfig.reducerType,
    financialAccounts,
    jointOwner: getJointOwner(jointOwners, financialAccountId),
    isMobile,
  })

  const firstAdHocContactOfType = type => adHocContacts.find(c => c.beneficiaryType === type)

  const updateAccount = () => {
    const submitData = {
      [`reviewed${section}`]: true,
    }
    return updateFinancialAccount(financialAccountId, submitData)
  }

  const initialData = {}
  const firstPrimary = firstAdHocContactOfType('Primary') || {}
  const firstContingent = firstAdHocContactOfType('Contingent') || {}

  const adHocContactIds = adHocContacts.map(ac => ac.id)
  const maskedContacts = maskRepeatedContacts(currentContacts, prospect.personContactId)
  const maskedAdHocContacts = maskedContacts.filter(mc => adHocContactIds.includes(mc.id))
    .map((mc) => {
      const ac = adHocContacts.find(c => c.id === mc.id)
      return ({ ...ac, ...mc })
    })

  maskedAdHocContacts.forEach((c,i) => {
    const type  = c.beneficiaryType !== 'Contingent' ? 'Default' : 'Contingent'
    let percentageValue =  c.sharePercentage

    initialData[`contact_${i}_name_${type}`] = c.name

    const percentageIsNull = percentageValue === null
    const isFirstContact = firstPrimary.id === c.id || firstContingent.id === c.id

    if (percentageIsNull) {
      percentageValue = isFirstContact ? 100 : 0
    }

    initialData[`contact_${i}_percentage_${type}`] = percentageValue
  })

  const {
    routeType,
  } = contactTypeConfig

  const filteredContacts = contactsFilteredByTypeRules(
    routeType,
    maskedContacts,
    prospect
  )

  const subType = currentAccount && currentAccount.financialAccount 
    && currentAccount.financialAccount.subType
  const isIndividual401k = subType === SCHWAB_IRA.ACCOUNT_TYPES.INDIVIDUAL_401K
  const upperTitle = isIndividual401k ? '401(k) Account' : financialAccountTypeConfig.DISPLAY
  
  return (
    <ContactsSection
      {...props}
      adHocContacts={maskedAdHocContacts}
      contacts={filteredContacts}
      contactType={routeType}
      isSuccessorDonor={isSuccessorDonor}
      description={[description(openModal)]}
      editFinancialAccount={editFinancialAccount}
      formName={title}
      financialAccountId={financialAccountId}
      financialAccountType={financialAccountType}
      initialData={initialData}
      goAddContactRoute={goOnboardingAddContact}
      goNextSection={handleContinueToNextSection}
      handleDeleteContactRole={handleDeleteContactRole}
      handleCreateContactRole={handleCreateContactRole}
      handleUpdateContactRole={handleUpdateContactRole}
      maxAdHocContacts={contactTypeConfig.maxContacts}
      prevPageInfo={prevPageInfo}
      sectionTitle={contactTypeConfig.DISPLAY}
      updateAccount={updateAccount}
      selfContactId={prospect.personContactId}
      upperTitle={upperTitle}
      role={contactTypeConfig.role} />
  )
}

ContactIndexBase.defaultProps = {
  currentContacts: [],
  currentFinancialAccountContacts: [],
  prospect: {},
  currentAccount: { financialAccount: {} },
}

ContactIndexBase.propTypes = {
  currentFinancialAccountContacts: PropTypes.array,
  addContactFinancialAccountRole: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
  currentContacts: PropTypes.array,
  goOnboardingAddContact: PropTypes.func.isRequired,
  jointOwners: PropTypes.array.isRequired,
  financialAccounts: PropTypes.array.isRequired,
  isMobile: PropTypes.bool.isRequired,
  openModal: PropTypes.func.isRequired,
  editFinancialAccount: PropTypes.func.isRequired,
  prospect: PropTypes.object,
  currentAccount: PropTypes.object,
}

export default ContactIndexBase



// WEBPACK FOOTER //
// ./src/components/Onboarding/Accounts/ContactIndexBase.jsx